import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { ArrowRight, Star } from "@phosphor-icons/react";

const WhyUsCard = ({ title, body }) => (
  <div className="border border-border bg-white p-6">
    <h3 className="font-heading text-xl mb-2">{title}</h3>
    <p className="text-[15px] text-muted-foreground leading-[1.65]">{body}</p>
  </div>
);

export default function WhyChoose() {
  return (
    <div className="App min-h-screen">
      <SEO
        title="Why teams choose ExactReach"
        description="Data built around your requirement, multiple sources, verified deliverability, and no long-term commitment. See why revenue teams pick ExactReach."
      />
      <Navbar />

      {/* Hero */}
      <section className="border-b border-border relative">
        <div className="absolute inset-0 grid-lines opacity-[0.35] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="flex items-center gap-2 mb-4">
            <Star size={22} weight="duotone" className="text-primary" />
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold">Why teams choose us</div>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[0.98] mb-6 max-w-3xl">
            Why teams choose ExactReach.
          </h1>
          <p className="text-[18px] text-muted-foreground max-w-2xl leading-[1.6]">
            Buying B2B data should not mean committing to another annual subscription, navigating complicated credit systems or spending hours cleaning records before they can be used. We make the process simpler.
          </p>
        </div>
      </section>

      {/* Cards */}
      <section className="py-24 md:py-32 border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-4">
            <WhyUsCard
              title="Data built around your requirement"
              body="We do not expect you to adjust your targeting to fit a fixed set of filters. Tell us who you want to reach, and we will build the audience around your geography, industry, company size, job functions, technologies and other specific criteria."
            />
            <WhyUsCard
              title="More than a single data source"
              body="No database provides complete coverage for every market or audience. We use multiple established data sources, supported by additional research where required, to improve coverage and handle requirements that standard platforms may struggle with."
            />
            <WhyUsCard
              title="Experienced research support"
              body="Our research team has years of experience working with detailed and unusual B2B targeting requirements. Whether you need a niche job function, a specific technology audience, a named account list or a difficult market, we review the requirement instead of simply returning whatever a standard search produces."
            />
            <WhyUsCard
              title="Verified data as standard"
              body="Every list ships with re-verified business emails and validated firmographics — no separate 'premium' tier, no unverified fallback. Optional phone verification and manual review can be added for more specialised requirements."
            />
            <WhyUsCard
              title="Review before you purchase"
              body="Before confirming an order, you can review how we have interpreted your targeting criteria, see the estimated audience volume and examine a sample of the available data. This gives you greater clarity before you commit."
            />
            <WhyUsCard
              title="No long-term commitment"
              body="Purchase the data you need for the campaign you are running today. There is no compulsory annual agreement, no seat-based pricing and no requirement to keep paying during months when you do not need additional records."
            />
            <WhyUsCard
              title="Clear and accountable delivery"
              body="Every order is delivered against an agreed targeting brief. Verification standards are clearly explained before purchase. No vague promises and no confusing credit calculations."
            />
            <div className="border-2 border-foreground bg-foreground text-background p-6 offset-shadow-primary">
              <h3 className="font-heading text-xl mb-2 text-background">Ready when you are.</h3>
              <p className="text-[15px] leading-[1.65] text-background/80 mb-4">Just targeted B2B data, prepared around the people your team actually wants to reach.</p>
              <Link to="/#query">
                <Button variant="secondary" className="rounded-sm bg-background text-foreground hover:bg-background/90" data-testid="why-choose-cta">
                  Check my audience <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
